import { useEffect, useRef, useState } from 'react';
import { CheckIcon, PlusCircle } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

import { CommandOptionsFeedback } from 'components/CommandOptionsFeedback';

import type { AsyncFilterOption } from './types';

const SEARCH_DEBOUNCE_MS = 250;

type DataTableAsyncFilterProps = {
    title: string;
    selectedValues: string[];
    onChange: (values: string[]) => void;
    loadOptions: (search: string, unresolvedValues: string[]) => Promise<AsyncFilterOption[]>;
};

/**
 * Même chip que `DataTableFacetedFilter`, mais les options viennent du SERVEUR à chaque frappe.
 *
 * C'est ce qui rend le filtre tenable sur un référentiel qui ne tient pas en mémoire : les
 * utilisateurs se comptent en milliers, les charger tous pour en cocher un ou deux ne passe pas à
 * l'échelle.
 *
 * Les libellés des valeurs SÉLECTIONNÉES sont mémorisés : sans ça, taper une nouvelle recherche vide
 * la liste des options et les chips retomberaient sur des identifiants bruts.
 */
export function DataTableAsyncFilter({ title, selectedValues, onChange, loadOptions }: DataTableAsyncFilterProps) {
    const [search, setSearch] = useState('');
    const [options, setOptions] = useState<AsyncFilterOption[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [labelsByValue, setLabelsByValue] = useState<Record<string, string>>({});

    // Une recherche plus ancienne peut répondre après une plus récente : on ignore les retours périmés.
    const latestSearchRef = useRef(0);

    /**
     * La liste d'options ne sert qu'à deux choses : remplir le panneau, et nommer les chips déjà
     * sélectionnées. Tant qu'aucune des deux n'est vraie, la charger est une requête pour rien — et
     * le bouton, lui, est rendu en permanence par la barre d'outils, donc chaque visite de la page
     * la déclenchait. Le filtre ACTIF reste chargé au montage : c'est ce qui évite que les chips
     * retombent sur des identifiants bruts au retour d'une URL partagée.
     */
    const shouldLoadOptions = isOpen || selectedValues.length > 0;

    /**
     * Les valeurs sélectionnées dont on ignore encore le libellé : une recherche vide ne rend que les
     * premières options du référentiel, qui ne contiennent pas forcément la sélection venue d'une URL.
     * On les demande explicitement, une fois — une fois nommées, cette clé redevient vide.
     */
    const unresolvedValuesKey = selectedValues.filter((value) => !labelsByValue[value]).join(',');

    useEffect(() => {
        if (!shouldLoadOptions) {
            return;
        }

        const searchId = ++latestSearchRef.current;

        // Dès la frappe, pas seulement à l'envoi de la requête : la fenêtre de debounce se passait
        // sans aucun retour visuel, la liste PRÉCÉDENTE restant affichée comme si elle était à jour.
        setIsLoading(true);

        const timeoutId = setTimeout(async () => {
            try {
                const loadedOptions = await loadOptions(
                    search,
                    unresolvedValuesKey ? unresolvedValuesKey.split(',') : [],
                );

                if (searchId !== latestSearchRef.current) {
                    return;
                }

                setOptions(loadedOptions);
                setLabelsByValue((previous) => ({
                    ...previous,
                    ...Object.fromEntries(loadedOptions.map(({ value, label }) => [value, label])),
                }));
            } finally {
                if (searchId === latestSearchRef.current) {
                    setIsLoading(false);
                }
            }
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timeoutId);
    }, [search, loadOptions, shouldLoadOptions, unresolvedValuesKey]);

    const toggle = (value: string) =>
        onChange(
            selectedValues.includes(value) ? selectedValues.filter((v) => v !== value) : [...selectedValues, value],
        );

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className={cn('h-8 border-dashed bg-filter', selectedValues.length > 0 && 'bg-filter-active')}
                >
                    <PlusCircle className="size-3.5" />
                    {title}
                    {selectedValues.length > 0 && (
                        <>
                            <Separator orientation="vertical" className="mx-1" />
                            <Badge variant="secondary" className="rounded-sm px-1 font-normal lg:hidden">
                                {selectedValues.length}
                            </Badge>
                            <div className="hidden space-x-1 lg:flex">
                                {selectedValues.length > 2 ? (
                                    <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                                        {selectedValues.length} sélectionnés
                                    </Badge>
                                ) : (
                                    selectedValues.map((value) => (
                                        <Badge key={value} variant="secondary" className="rounded-sm px-1 font-normal">
                                            <span>{labelsByValue[value] ?? value}</span>
                                        </Badge>
                                    ))
                                )}
                            </div>
                        </>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0" align="start">
                {/* `shouldFilter={false}` : c'est le serveur qui filtre, pas la liste locale. */}
                <Command shouldFilter={false}>
                    <CommandInput
                        placeholder={`Rechercher ${title.toLowerCase()}...`}
                        value={search}
                        onValueChange={setSearch}
                    />
                    <CommandList>
                        {/* Même retour visuel que le champ (`TotemSelect`) : l'indicateur circulaire
                            remplace la liste, au lieu de laisser croire que les options affichées
                            correspondent à ce qui vient d'être tapé. */}
                        <CommandOptionsFeedback isLoading={isLoading} emptyLabel="Aucun résultat." />
                        <CommandGroup>
                            {(isLoading ? [] : options).map((option) => {
                                const isSelected = selectedValues.includes(option.value);

                                return (
                                    <CommandItem
                                        key={option.value}
                                        onSelect={() => toggle(option.value)}
                                        data-checked={isSelected}
                                    >
                                        <div
                                            className={cn(
                                                'flex size-4 items-center justify-center rounded-sm border border-primary',
                                                isSelected
                                                    ? 'bg-primary text-primary-foreground'
                                                    : 'opacity-50 [&_svg]:invisible',
                                            )}
                                        >
                                            <CheckIcon className="size-3" />
                                        </div>
                                        <span>{option.label}</span>
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                    {selectedValues.length > 0 && (
                        <>
                            <Separator />
                            <CommandGroup>
                                <CommandItem className="justify-center text-center" onSelect={() => onChange([])}>
                                    Effacer les filtres
                                </CommandItem>
                            </CommandGroup>
                        </>
                    )}
                </Command>
            </PopoverContent>
        </Popover>
    );
}
